/**
 * PreferredDriverStatus — small live-status card for the vendor dashboard.
 * Polls /vendor-admin/preferred-driver/status every 30s and shows a colored
 * pill so the vendor knows at a glance whether their pinned driver is on shift.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Bike, User, Star, ArrowRight, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { vendorAdminApi } from '../services/api';

const STATE_STYLES = {
  online: {
    dot: 'bg-emerald-500',
    ring: 'ring-emerald-500/30 animate-pulse',
    text: 'text-emerald-700',
    label: 'On shift',
    pill: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  idle: {
    dot: 'bg-amber-400',
    ring: 'ring-amber-400/30',
    text: 'text-amber-700',
    label: 'Idle',
    pill: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  offline: {
    dot: 'bg-slate-300',
    ring: 'ring-slate-300/30',
    text: 'text-slate-500',
    label: 'Offline',
    pill: 'bg-slate-50 text-slate-500 border-slate-200',
  },
};

const humanize = (sec) => {
  if (sec == null) return 'never';
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
  return `${Math.round(sec / 86400)}d ago`;
};

const PreferredDriverStatus = ({ vendorId, impersonating }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);
  // Track the last observed driver state so we can detect the moment the
  // pinned driver flips from offline/idle → online and pop a celebratory toast.
  const lastStateRef = useRef(null);
  const lastDriverIdRef = useRef(null);

  useEffect(() => {
    mounted.current = true;
    // Reset the "last state" cache whenever the vendor context changes so
    // switching stores doesn't fire a stale toast.
    lastStateRef.current = null;
    lastDriverIdRef.current = null;

    const load = async () => {
      try {
        const r = await vendorAdminApi.preferredDriverStatus(impersonating ? vendorId : undefined);
        if (!mounted.current) return;
        const nextState = r?.driver?.state || null;
        const nextDriverId = r?.driver?.driver_id || null;
        const prevState = lastStateRef.current;
        const sameDriver = lastDriverIdRef.current === nextDriverId;
        // Only fire once per online transition and only after the first load.
        if (prevState && sameDriver && prevState !== 'online' && nextState === 'online') {
          const name = r.driver.name || 'Preferred driver';
          const plate = r.driver.vehicle_plate ? ` (${r.driver.vehicle_plate})` : '';
          toast.success(`${name}${plate} just came online`, {
            description: 'Rush new orders now — they\u2019ll be assigned first.',
            duration: 8000,
          });
        }
        lastStateRef.current = nextState;
        lastDriverIdRef.current = nextDriverId;
        setData(r);
      } catch {
        if (mounted.current) setData({ preferred_driver_id: null, driver: null });
      } finally {
        if (mounted.current) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { mounted.current = false; clearInterval(t); };
  }, [vendorId, impersonating]);

  if (loading && !data) {
    return (
      <section
        className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-3"
        data-testid="preferred-driver-status-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        <span className="text-sm text-slate-500">Loading driver status…</span>
      </section>
    );
  }

  // No preferred driver pinned yet — nudge the vendor to configure one.
  if (!data?.driver) {
    return (
      <section
        className="bg-white border border-dashed border-slate-300 rounded-2xl p-5"
        data-testid="preferred-driver-status-empty"
      >
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center">
            <User className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-900">No preferred driver</div>
            <p className="text-sm text-slate-500 mt-0.5">
              Pin a favourite driver so ShipitNow tries them first for your orders.
            </p>
          </div>
          <Link
            to="/vendor-admin/settings"
            className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:text-emerald-800"
            data-testid="preferred-driver-status-configure"
          >
            Configure <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </section>
    );
  }

  const d = data.driver;
  const style = STATE_STYLES[d.state] || STATE_STYLES.offline;
  const initials = (d.name || 'D').split(' ').map((p) => p[0]).slice(0, 2).join('');
  const suffix = d.vehicle_plate ? d.vehicle_plate : d.driver_id ? `#${String(d.driver_id).slice(-4)}` : '';

  return (
    <section
      className="bg-white border border-slate-200 rounded-2xl p-5"
      data-testid="preferred-driver-status"
      data-driver-state={d.state}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
          <h2 className="font-semibold text-slate-900">Preferred driver</h2>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 text-xs font-medium border rounded-full px-2 py-0.5 ${style.pill}`}
          data-testid="preferred-driver-status-pill"
        >
          <span className={`h-2 w-2 rounded-full ring-2 ${style.dot} ${style.ring}`} />
          {style.label}
        </span>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative">
          <div className="h-11 w-11 rounded-full bg-emerald-600 text-white flex items-center justify-center font-semibold">
            {initials}
          </div>
          <span
            className={`absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-white ${style.dot}`}
            aria-hidden
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-slate-900 truncate" data-testid="preferred-driver-status-name">
            {d.name}
            {suffix && <span className="ml-1.5 text-slate-400 font-normal">• {suffix}</span>}
          </div>
          <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="inline-flex items-center gap-0.5">
              <Star className="h-3 w-3 text-amber-400 fill-amber-400" />
              {(d.rating || 5).toFixed(1)}
            </span>
            <span className="text-slate-300">·</span>
            <span className="inline-flex items-center gap-1">
              <Bike className="h-3 w-3" /> {d.vehicle_type || 'Vehicle'}
            </span>
            <span className="text-slate-300">·</span>
            <span className={style.text}>
              {d.state === 'online' ? `Ping ${humanize(d.seconds_since_ping)}` : `Last seen ${humanize(d.seconds_since_ping)}`}
            </span>
          </div>
        </div>
        <div className="text-right">
          <div
            className="text-2xl font-semibold text-slate-900 leading-none"
            data-testid="preferred-driver-active-orders"
          >
            {d.active_orders}
          </div>
          <div className="text-[11px] text-slate-500 uppercase tracking-wide mt-1">
            Active
          </div>
        </div>
      </div>

      {d.state === 'idle' && (
        <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Driver is marked online but hasn&apos;t pinged recently — new orders may fall back to the nearest driver.
        </div>
      )}
      {d.state === 'offline' && (
        <div className="mt-3 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
          Driver is offline. New orders will go to the nearest available driver until they&apos;re back.
        </div>
      )}
    </section>
  );
};

export default PreferredDriverStatus;
